import { useCallback, useMemo, useState } from 'react'

import { DropdownOption, DropdownValue } from '@inno-ui/dropdown'
import {
  GlobeLineIcon,
  MultiplyCircleLineIcon,
  PinLocationLineIcon
} from '@inno-ui/icons'
import { useWidgetMutations } from 'modules/desktop/modules/widgets/hooks'
import { useTranslation } from 'react-i18next'
import { WidgetSize } from 'shared/constants/widgets'
import { PersistedWidgetSpec } from 'shared/types/widgets'

import { tzName } from './ClockWidget.utils'
import { ClockConfigTypes } from './components'

const ALL_TIMEZONES: string[] = Intl.supportedValuesOf
  ? Intl.supportedValuesOf('timeZone')
  : [
      'UTC',
      'Europe/Berlin',
      'Europe/Istanbul',
      'Europe/London',
      'Asia/Calcutta',
      'America/New_York'
    ]

const getDefaultConfig = (): ClockConfigTypes => {
  const primaryTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  return {
    primaryTimezone,
    secondaryTimezones: []
  }
}

const ensureSlots = (timezones: string[], maxSlots: number): string[] => {
  const result = [...timezones]
  while (result.length < maxSlots) {
    result.push('')
  }
  return result.slice(0, maxSlots)
}

export const useClockWidget = (widget: PersistedWidgetSpec) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'widgets.clockWidget'
  })
  const maxSlots = widget.size === WidgetSize.SMALL ? 3 : 4
  const initialConfig =
    Object.keys(widget.config ?? {}).length > 0
      ? (widget.config as ClockConfigTypes)
      : getDefaultConfig()

  const initialSecondaryTimezones = initialConfig.secondaryTimezones || []
  const [config, setConfig] = useState<ClockConfigTypes>({
    ...initialConfig,
    secondaryTimezones: ensureSlots(initialSecondaryTimezones, maxSlots)
  })
  const { updateWidget } = useWidgetMutations()

  const handleConfigChange = useCallback(
    <T extends Record<string, unknown>>(newConfig: T) => {
      const clockConfig = newConfig as unknown as ClockConfigTypes
      const maxSlots = widget.size === WidgetSize.SMALL ? 3 : 4
      const configWithSlots = {
        ...clockConfig,
        secondaryTimezones: ensureSlots(
          clockConfig.secondaryTimezones || [],
          maxSlots
        )
      }
      setConfig(configWithSlots)
      if (!('id' in widget)) return

      const widgetId = widget.id
      updateWidget(widgetId, item => ({
        ...item,
        config: configWithSlots
      }))
    },
    [widget, updateWidget]
  )

  const timezoneOptions = useMemo(
    () => [
      {
        label: t('not_show'),
        value: '',
        icon: MultiplyCircleLineIcon
      },
      {
        label: tzName('UTC'),
        value: 'UTC',
        icon: GlobeLineIcon
      },
      ...ALL_TIMEZONES.filter(tz => tz !== 'UTC').map(tz => ({
        label: tzName(tz),
        value: tz,
        icon: GlobeLineIcon
      }))
    ],
    [t]
  )

  const contextMenuOptions = useMemo(() => {
    const usedTimezones = new Set([
      config.primaryTimezone,
      ...config.secondaryTimezones.filter(tz => tz !== '')
    ])

    const maxSlots = widget.size === WidgetSize.SMALL ? 3 : 4
    const worldClocks = ensureSlots(config.secondaryTimezones, maxSlots).map(
      (tz, index) => {
        const selectedOption =
          tz === ''
            ? timezoneOptions[0]
            : timezoneOptions.find(opt => opt.value === tz) ||
              timezoneOptions[0]
        return {
          icon: tz ? GlobeLineIcon : MultiplyCircleLineIcon,
          label: tz ? tzName(tz) : t('not_show'),
          dropdownListProps: {
            isSearchable: true,
            listWidth: '250px',
            height: '250px',
            value: selectedOption,
            clear: true,
            onClose: () => {},
            items: timezoneOptions.filter(
              opt =>
                opt.value === '' ||
                !usedTimezones.has(opt.value) ||
                opt.value === tz
            ),
            onChange: (val: DropdownValue) => {
              if (!val) return
              const selectedValue = (val as DropdownOption).value
              const maxSlots = widget.size === WidgetSize.SMALL ? 3 : 4
              const currentSlots = ensureSlots(
                [...config.secondaryTimezones],
                maxSlots
              )
              const updatedSlots = [...currentSlots]
              updatedSlots[index] = selectedValue
              handleConfigChange({
                ...config,
                secondaryTimezones: updatedSlots
              })
            }
          }
        }
      }
    )

    return [
      { label: t('clock'), disabled: true },
      {
        icon: PinLocationLineIcon,
        label: tzName(config.primaryTimezone),
        dropdownListProps: {
          isSearchable: true,
          listWidth: '250px',
          height: '250px',
          value: timezoneOptions.find(
            opt => opt.value === config.primaryTimezone
          ),
          onClose: () => {},
          items: timezoneOptions.filter(opt => opt.value !== ''),
          onChange: (val: DropdownValue) => {
            if (!val) return
            handleConfigChange({
              ...config,
              primaryTimezone: (val as DropdownOption).value
            })
          }
        }
      },
      { label: t('world_clocks'), disabled: true },
      ...worldClocks
    ]
  }, [config, handleConfigChange, t, timezoneOptions, widget.size])

  return {
    config,
    handleConfigChange,
    contextMenuOptions
  }
}
